import classNames from 'classnames';
import { kebabCase } from 'lodash';
import { Input, InputGroupAddon, InputGroupText, InputGroup } from 'reactstrap';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { enabledSelector, labelSelector, unitsSelector, valueSelector, conformanceSelector, newPathSelector } from '../selectors/index';
import entityComposition from '../../common/classes/entityComposition';
import Label from './label';
import ConformanceTooltip from './conformanceTooltip';
import logValueFunc from '../commonActions/logValue';
import onChangeFunc from '../commonActions/onChange';
import localChangeSelector from '../selectors/localChangeSelector';
const setMinIntegerDigits = (value, minimumIntegerDigits) => {
  if (minimumIntegerDigits === 0) {
    return value;
  }
  const isNegative = value.toString().substring(0, 1) === '-';
  const absoluteValue = isNegative ? value.toString().substring(1) : value;
  // We only want to deal with the integers
  const parts = absoluteValue.toString().split('.');
  const integers = parts[0].padStart(minimumIntegerDigits, 0);
  // See if there are decimal units that need to be appended
  const repackaged = parts.length === 1 ? integers : [integers, parts[1]].join('.');
  // Reattach the negative sign
  return isNegative ? `-${repackaged}` : repackaged;
};
const setMaxIntegerDigits = (value, maximumIntegerDigits) => {
  if (maximumIntegerDigits === 0) {
    return value;
  }
  const stringValue = value.toString();
  const isNegative = stringValue.substring(0, 1) === '-';
  const absoluteValue = isNegative ? stringValue.substring(1) : stringValue;
  // We only want to deal with the integers
  const parts = absoluteValue.split('.');
  let integers = parts[0];
  if (integers.length > maximumIntegerDigits) {
    integers = integers.substring(0, maximumIntegerDigits);
  }
  // See if there are decimal units that need to be appended
  const repackaged = parts.length === 1 ? integers : [integers, parts[1]].join('.');
  // Reattach the negative sign
  return isNegative ? `-${repackaged}` : repackaged;
};
const setFractionDigits = (value, maximumFractionDigits, minimumFractionDigits) => {
  // Handle Maximum by rounding
  const exp = 10 ** maximumFractionDigits;
  const roundedValue = Math.round(value * exp) / exp;
  // Handle Minimum by appending 0's if need b
  const numberOfFractionDigits = roundedValue.toString().split('.')[1];
  if (numberOfFractionDigits >= minimumFractionDigits) {
    return roundedValue.toString();
  }
  return roundedValue.toFixed(minimumFractionDigits);
};
const getDisplayValue = (value, maximumFractionDigits, minimumFractionDigits, minimumIntegerDigits, maximumIntegerDigits) => {
  if (value === null || value === '') {
    return null;
  }
  const fraction = setFractionDigits(value, maximumFractionDigits, minimumFractionDigits);
  let finalValue = setMinIntegerDigits(fraction, minimumIntegerDigits);
  finalValue = setMaxIntegerDigits(finalValue, maximumIntegerDigits);
  return finalValue;
};
const NumberField = ({
  conformanceText: _conformanceText = null,
  displayUnit: _displayUnit = '',
  enabled,
  internalLabel: _internalLabel = null,
  label: _label = null,
  maximumFractionDigits: _maximumFractionDigits = 2,
  minimumFractionDigits: _minimumFractionDigits = 0,
  minimumIntegerDigits: _minimumIntegerDigits = 0,
  maximumIntegerDigits: _maximumIntegerDigits = 0,
  onChange,
  placeholder: _placeholder = '',
  testingIdentifier: _testingIdentifier = 'default-id',
  value: propsValue = null,
  path,
  logValue,
  operationComplete,
  operationPending,
  localChangeFunc
}) => {
  // a direct reference to the input field.
  const valueRef = useRef();
  // this is used to validate the input.
  const [lastValue, setLastValue] = useState(`${propsValue}`);
  const showConformance = !!_conformanceText;
  const inputAddon = _internalLabel || _displayUnit || '';
  const displayValue = getDisplayValue(propsValue, _maximumFractionDigits, _minimumFractionDigits, _minimumIntegerDigits, _maximumIntegerDigits);
  const handleBlur = async () => {
    let {
      value
    } = valueRef.current;
    if (value === '' || value === null) {
      // Allow Clearing input
      value = null;
    } else {
      // Validate input, or reject by returning previous
      value = value.replace(/[^\d.-]/g, '');
    }
    const updatedValue = getDisplayValue(value, _maximumFractionDigits, _minimumFractionDigits, _minimumIntegerDigits, _maximumIntegerDigits);
    let saveValue = null;
    if (updatedValue !== null) {
      saveValue = !Number.isNaN(Number(updatedValue)) ? Number.parseFloat(updatedValue) : 0;
    }
    if (saveValue !== propsValue) {
      var _valueRef$current;
      // let redux know we have a complex operation
      // afoot.
      await operationPending();
      const change = localChangeFunc({
        path,
        value: saveValue
      });
      await onChange(change);
      const log = {
        value: saveValue,
        displayValue: `${updatedValue} ${_displayUnit || ''}`
      };
      // log the display value.
      await logValue(log);
      // done with complex operation.
      if ((valueRef === null || valueRef === void 0 || (_valueRef$current = valueRef.current) === null || _valueRef$current === void 0 ? void 0 : _valueRef$current.value) !== updatedValue) {
        // we updated props, we should update value here.
        if (updatedValue === null) {
          valueRef.current.value = '';
        }
        if (valueRef.current !== undefined) {
          valueRef.current.value = updatedValue;
        }
      }
      return operationComplete();
    }
    return undefined;
  };
  const handleChange = async e => {
    const {
      value: inputValue
    } = e.target;
    let updatedValue = '';
    if (inputValue === '' || inputValue === null) {
      // Allow Clearing input
      updatedValue = null;
    } else {
      // Validate input, or reject by returning previous
      updatedValue = /^-?\d*[.]?\d*$/.exec(inputValue.toString()) ? inputValue : lastValue;
      if (updatedValue === 'null') {
        updatedValue = null;
      }
    }
    if (updatedValue !== inputValue) {
      if (updatedValue === null) {
        valueRef.current.value = '';
      } else {
        valueRef.current.value = updatedValue;
      }
      setLastValue(updatedValue);
    }
  };

  // sometimes value can change outside of
  // the input - via behaviors for example.
  useEffect(() => {
    var _valueRef$current2;
    if ((valueRef === null || valueRef === void 0 || (_valueRef$current2 = valueRef.current) === null || _valueRef$current2 === void 0 ? void 0 : _valueRef$current2.value) !== displayValue) {
      // we updated props, we should update value here.
      if (valueRef.current !== undefined) {
        valueRef.current.value = displayValue;
      }
    }
  }, [displayValue]);
  const className = 't-ft';
  // Path-stable id (not uuid): collection remounts must keep the same target so
  // ConformanceTooltip / reactstrap do not throw "target could not be identified".
  const id = useMemo(() => {
    const pathSlug = kebabCase(path);
    const normalizedLabel = typeof _label === 'string' ? _label.trim() : _label;
    if (!normalizedLabel) {
      return `input-${pathSlug}`;
    }
    const labelSlug = kebabCase(normalizedLabel);
    return labelSlug === pathSlug ? `input-${pathSlug}` : `input-${labelSlug}-${pathSlug}`;
  }, [_label, path]);
  const testID = `${className}-${kebabCase(_testingIdentifier)}`;
  const formClasses = classNames({
    [`${className}-${id}`]: true,
    [`${testID}-input`]: true,
    disabled: !enabled,
    'tqc-control': true,
    'tqc-number-field': true
  });
  return /*#__PURE__*/React.createElement("div", {
    className: `${testID}-form`
  }, _label && /*#__PURE__*/React.createElement(Label, {
    value: _label,
    style: {
      height: '32px'
    }
  }), /*#__PURE__*/React.createElement(InputGroup, null, /*#__PURE__*/React.createElement(Input, {
    id: id,
    "data-testid": testID,
    type: "text",
    placeholder: _placeholder,
    onChange: handleChange,
    onBlur: handleBlur,
    className: formClasses,
    defaultValue: displayValue || '',
    innerRef: valueRef,
    disabled: !enabled,
    invalid: showConformance
  }), inputAddon ? /*#__PURE__*/React.createElement(InputGroupAddon, {
    addonType: "append"
  }, /*#__PURE__*/React.createElement(InputGroupText, null, inputAddon)) : null), /*#__PURE__*/React.createElement(ConformanceTooltip, {
    errorText: _conformanceText,
    target: id
  }));
};
NumberField.propTypes = {
  path: PropTypes.string.isRequired,
  conformanceText: PropTypes.string,
  enabled: PropTypes.bool.isRequired,
  displayUnit: PropTypes.string,
  internalLabel: PropTypes.string,
  label: PropTypes.string,
  maximumFractionDigits: PropTypes.number,
  // used by getDisplayValue
  minimumIntegerDigits: PropTypes.number,
  maximumIntegerDigits: PropTypes.number,
  // used by getDisplayValue
  minimumFractionDigits: PropTypes.number,
  // used by getDisplayValue
  onChange: PropTypes.func.isRequired,
  logValue: PropTypes.func.isRequired,
  operationPending: PropTypes.func.isRequired,
  operationComplete: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  testingIdentifier: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  localChangeFunc: PropTypes.func.isRequired
};
export { NumberField as UncomposedNumberField };
export default entityComposition(NumberField, [], {
  conformanceText: conformanceSelector,
  displayUnit: unitsSelector,
  enabled: enabledSelector,
  label: labelSelector,
  value: valueSelector,
  path: newPathSelector,
  logValue: logValueFunc,
  onChange: onChangeFunc,
  localChangeFunc: localChangeSelector
});